import { ICampaignDetails } from '@www/_models/campaign/campaign-details';
import { decodeHtmlEntities, getBrandConsentTexts, jsonToHtml } from '@loreal-www-frontend/shared';
import {
  CampaignHeroSectionElementDetailsUnion,
  CampaignHeroSectionElementTypeEnum,
  ICampaignHeroSectionDetails,
} from '@www/_models';

export function mapCampaignDetails(details: ICampaignDetails): ICampaignDetails {
  // Panel zapisuje pola „prostego tekstu" (nagłówki, etykiety, teksty przycisków, nazwy pól formularza)
  // zakodowane HTML-owo — `&` trafia do bazy jako `&amp;`. Na froncie te pola renderujemy interpolacją
  // `{{ }}`, więc encja wypisałaby się dosłownie. Dekodujemy je tu, w jednym miejscu wspólnym dla
  // /kampanii i /preview. Treści z CKEditor-a idą przez `jsonToHtml` + `[innerHTML]` i encji nie ruszamy.
  const _decodeTextProp = <TKey extends string, TObject extends Partial<Record<TKey, string | null>>>(
    object: TObject | null | undefined,
    key: TKey,
  ): void => {
    const value = object?.[key];
    if (object && typeof value === 'string' && value) {
      object[key] = decodeHtmlEntities(value) as TObject[TKey];
    }
  };

  const _processColumnElements = (
    elements: {
      0?: CampaignHeroSectionElementDetailsUnion[];
      1?: CampaignHeroSectionElementDetailsUnion[];
      2?: CampaignHeroSectionElementDetailsUnion[];
      3?: CampaignHeroSectionElementDetailsUnion[];
      4?: CampaignHeroSectionElementDetailsUnion[];
    },
    columnIndex: '0' | '1' | '2' | '3' | '4',
  ) => {
    if (elements[columnIndex] && elements[columnIndex]?.length) {
      elements[columnIndex] = (elements[columnIndex] as CampaignHeroSectionElementDetailsUnion[]).map(
        (v: CampaignHeroSectionElementDetailsUnion) => {
          if (v?.type === CampaignHeroSectionElementTypeEnum.TEXT && v?.data) {
            if (Object.keys(v.data).length === 0) {
              v.data = '';
            } else {
              try {
                v.data = v?.data ? jsonToHtml(v?.data) : '';
              } catch (e) {
                console.error('Error parsing hero_section.elements.data', e, v?.data);
              }
            }
          }

          if (v?.type === CampaignHeroSectionElementTypeEnum.HEADER) {
            _decodeTextProp(v.data, 'text');
          }

          if (v?.type === CampaignHeroSectionElementTypeEnum.BUTTON) {
            _decodeTextProp(v.data, 'buttonText');
          }

          return v;
        },
      );
    }
  };

  _decodeTextProp(details, 'headline');
  _decodeTextProp(details, 'meta_title');
  _decodeTextProp(details, 'meta_description');

  if (details.thank_you_page) {
    // @ts-ignore
    details.thank_you_page = JSON.parse(details.thank_you_page as string);

    _decodeTextProp(details.thank_you_page?.header1, 'text');
    _decodeTextProp(details.thank_you_page?.header2, 'text');

    if (details.thank_you_page?.sections?.length) {
      details.thank_you_page.sections = details.thank_you_page?.sections.map((section: ICampaignHeroSectionDetails) => {
        const mapped: ICampaignHeroSectionDetails = {
          columns: section.columns,
          elements: section.elements,
        };
        (['0', '1', '2', '3', '4'] as ('0' | '1' | '2' | '3' | '4')[]).forEach((columnIndex) => {
          _processColumnElements(section.elements, columnIndex);
        });
        return mapped;
      });
    }
  }

  if (details.hero_section) {
    // @ts-ignore
    details.hero_section = JSON.parse(details.hero_section as string);

    _decodeTextProp(details.hero_section?.label, 'text');
    _decodeTextProp(details.hero_section?.header1, 'text');
    _decodeTextProp(details.hero_section?.header2, 'text');
    _decodeTextProp(details.hero_section?.cta, 'text');
    _decodeTextProp(details.hero_section?.belowButton, 'belowButtonText');

    if (details.hero_section?.footerSection?.textSection) {
      let textSection = null;

      try {
        textSection = details.hero_section?.footerSection?.textSection
          ? jsonToHtml(details.hero_section?.footerSection?.textSection)
          : null;
      } catch (e) {
        console.error(
          'Error parsing hero_section.footerSection.textSection',
          e,
          details.hero_section?.footerSection?.textSection,
        );
      }

      details.hero_section.footerSection = {
        socials: details.hero_section?.footerSection?.socials,
        textSection: textSection,
      };
    }

    if (details.hero_section?.sections?.length) {
      details.hero_section.sections = details.hero_section?.sections.map((section: ICampaignHeroSectionDetails) => {
        const mapped: ICampaignHeroSectionDetails = {
          columns: section.columns,
          elements: section.elements,
        };
        (['0', '1', '2', '3', '4'] as ('0' | '1' | '2' | '3' | '4')[]).forEach((columnIndex) => {
          _processColumnElements(section.elements, columnIndex);
        });
        return mapped;
      });
    }
  }

  if (details.popup_finish_text) {
    try {
      details.popup_finish_text = jsonToHtml(details.popup_finish_text);
    } catch (e) {
      console.error('Error parsing popup_finish_text', e, details.popup_finish_text);
    }
  }

  if (details.data_administration_text) {
    try {
      details.data_administration_text = jsonToHtml(details.data_administration_text);
    } catch (e) {
      console.error('Error parsing data_administration_text', e, details.data_administration_text);
    }
  }

  if (details.disclaimer_text) {
    try {
      details.disclaimer_text = jsonToHtml(details.disclaimer_text);
    } catch (e) {
      console.error('Error parsing disclaimer_text', e, details.disclaimer_text);
    }
  }

  // Marki predefiniowane: treść zgód bierzemy z repo (CONSENT_TEXTS), nie z backendu — zmiana tekstu
  // w jednym miejscu aktualizuje wszystkie landingi danej marki bez edycji każdej kampanii w panelu.
  // Marka spoza listy zostaje przy treści zapisanej w backendzie (powyżej).
  const brandConsentTexts = getBrandConsentTexts(details.brand?.id);

  if (brandConsentTexts) {
    details.disclaimer_text = brandConsentTexts.disclaimerText;
    details.data_administration_text = brandConsentTexts.dataAdministrationText;
  }

  if (details.fields?.length) {
    details.fields = details.fields.map((field) => {
      const mapped = { ...field, name: decodeHtmlEntities(field.name) };

      if (!field.metadata) return mapped;

      return {
        ...mapped,
        metadata: {
          ...field.metadata,
          ...(field.metadata.description ? { description: decodeHtmlEntities(field.metadata.description) } : {}),
          ...(field.metadata.questions ? { questions: field.metadata.questions.map(decodeHtmlEntities) } : {}),
        },
      };
    });
  }

  return details;
}
